<script setup>
import {Priority, PriorityLabel} from "../../choices";
import {GenderLabel} from "../../../../choices";
import Hospital from "./td/Hospital.vue";
import {unitType, ventilated} from "../../utils";
import {computed} from "vue";
import Status from "./td/Status.vue";
import {mdiGestureTap} from "@mdi/js";
import {pgettext} from "../../../../utils/translate";

const props = defineProps({
    patient: {
        type: Object,
        required: true
    },
    now: {
        type: Object,
        required: true
    },
    member: {
        type: Object,
        required: true
    },
    alertList: {
        type: Array,
        required: true
    },
})
const emit = defineEmits(['displayActionsModal', 'setEditedTransportData', 'openReplyModal'])

const priorityColor = computed(() => {
    if (!props.patient.transportPriority) {
        return null
    }
    if (props.patient.transportPriority === Priority.III) {
        return 'green'
    } else if (props.patient.transportPriority === Priority.IIB) {
        return 'yellow'
    } else if (props.patient.transportPriority === Priority.IV) {
        return 'black'
    } else {
        return 'red'
    }
})

function displayActionsModal() {
    emit('displayActionsModal', props.patient)
}

function handleSetEditedTransportData(patient) {
    emit('setEditedTransportData', patient)
}

function handleOpenReplyModal(data, patient) {
    emit('openReplyModal', data, patient)
}

</script>

<template>
    <v-card density="compact" variant="elevated" class="d-flex flex-column flex-sm-row pa-1 mb-1"
            :color="priorityColor">
        <div class="d-flex">
            <v-card-title
                class="d-flex flex-grow-1 flex-row align-center ga-3 align-sm-stretch flex-sm-column ga-sm-0 font-weight-bold pa-0 pr-1 pl-1 pb-1 pl-sm-0 pb-sm-0 patient-title">
                <div class="text-center">{{ patient.ticPrefix }}{{ patient.ticNumber }}</div>
                <div class="text-center" v-if="patient.transportPriority">
                    <span class="mr-2">{{ pgettext('label', 'Transport Priority') }}:</span>
                    <span>{{ PriorityLabel[patient.transportPriority] }}</span>
                </div>
                <div class="text-center">
                    <span>{{ GenderLabel[patient.gender] }}</span>
                    <span v-if="patient.isChild">
                        {{ ' (' }}{{ gettext('child').toUpperCase() }}{{ ')' }}
                    </span>
                </div>
                <v-card-actions class="pa-0 flex-sm-grow-1 align-stretch patient-actions">
                    <v-btn
                        variant="flat"
                        class="h-auto font-weight-bold w-auto"
                        block
                        :prepend-icon="mdiGestureTap"
                        @click="displayActionsModal">
                        <span class="text-wrap">{{ pgettext('label', 'Options') }}</span>
                    </v-btn>
                </v-card-actions>
            </v-card-title>
        </div>
        <v-card-text class="pa-0">
            <v-sheet class="h-100 pa-1 patient-data">
                <dl>
                    <template v-if="patient.diagnose">
                        <dt>{{ pgettext('label', 'Diagnose') }}</dt>
                        <dd>
                            <p>{{ patient.diagnose }}</p>
                        </dd>
                    </template>
                    <template v-if="ventilated(patient.ventilated) !== null">
                        <dt>{{ pgettext('label', 'ALV') }}</dt>
                        <dd>
                            <div>{{ ventilated(patient.ventilated) }}</div>
                        </dd>
                    </template>
                    <template v-if="patient.unitType">
                        <dt>{{ pgettext('label', 'Unit Type') }}</dt>
                        <dd>
                            <div>{{ unitType(patient.unitType, patient.unitTypeSpec) }}</div>
                        </dd>
                    </template>
                    <template v-if="patient.callSign">
                        <dt>{{ pgettext('label', 'Call Sign / LP') }}</dt>
                        <dd>
                            <div>{{ patient.callSign }}</div>
                        </dd>
                    </template>
                    <template v-if="patient.hospitalType">
                        <dt>{{ pgettext('label', 'Hospital Type') }}</dt>
                        <dd>
                            <div>{{ patient.hospitalType }}</div>
                        </dd>
                    </template>
                    <template v-if="patient.hospitalType">
                        <dt>{{ pgettext('label', 'Destination Hospital') }}</dt>
                        <dd>
                            <div>
                                <Hospital :patient="patient"
                                          :member="member"
                                          :now="now"
                                          @setEditedTransportData="handleSetEditedTransportData"
                                          @openReplyModal="handleOpenReplyModal"/>
                            </div>
                        </dd>
                    </template>
                    <dt>{{ pgettext('label', 'Transport status') }}</dt>
                    <dd>
                        <div>
                            <Status :patient="patient"
                                    :member="member"
                                    :alertList="alertList"/>
                        </div>
                    </dd>
                </dl>
            </v-sheet>
        </v-card-text>
    </v-card>
</template>

<style scoped>
dl {
    display: grid;
    grid-template-columns: auto auto;
    grid-auto-rows: auto;
}

dt {
    text-wrap: wrap;
}

dd {
    text-align: center;
    display: flex;
    justify-content: center;
    align-items: center;
}

dt, dd {
    border-bottom: 1px solid lightgray;
}

p {
    text-wrap: auto;
}

@media screen and (max-width: 599px) {
    dl {
        grid-template-columns: auto auto;
    }

    .patient-title {
        font-size: 1.1rem;
        line-height: 1.25rem;
        min-width: 0;
    }

    .patient-data {
        min-height: 64px;
    }

    .patient-actions {
        order: -1;
        min-height: 44px;
    }
}

@media screen and (min-width: 600px) {
    .patient-title {
        min-width: 135px;
    }
}
</style>